import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Badge, Table, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronUp, User } from "lucide-react";
import { Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { MecordColors } from "Constants/Color";
import { OrderAndSalesAxios } from "api/orderandsalesaxios";
import moment from "moment";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { handleServiceQuotationStatus } from "slices/quotation/serviceThunk";
import { quotationStatusOptions } from "Common/Components/StatusDropdown/statusOptions";
import LoadingSpinner from "components/LoadingSpinner";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import StatusRemarksHistory from "pages/Enquiry/Components/StatusRemarksHistory";
import CancelBackButton from "Common/Components/Buttons/CancelBackButton";
import { AttachmentList } from "components/DocumentListing/AttachmentListing";
import CancelButton from "Common/Components/Buttons/CancelButton";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import DOMPurify from "dompurify";
import ViewServiceAccessoriesModal from "pages/QuotationV2/Form/Components/ViewServiceAccessoriesModal";
import { mapApiAccessoryToItem } from "pages/QuotationV2/Form/Components/ServiceAccessoryRow";
import {
  DETAILS_BG,
  filterPopoverStyles,
  tableStyles,
} from "Constants/Style";

const ViewServiceQuotation = ({ id: propId }: { id?: string }) => {
  const navigate = useNavigate();
  const { id: urlId } = useParams();
  const id = propId || urlId;
  const dispatch = useDispatch<any>();
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [quotation, setQuotation] = useState<any>(null);
  const [displayedQuotation, setDisplayedQuotation] = useState<any>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [descriptionModalContent, setDescriptionModalContent] =
    useState("");
  const [descriptionModalServiceName, setDescriptionModalServiceName] =
    useState("");
  const [showAdditionalDetailsModal, setShowAdditionalDetailsModal] =
    useState(false);
  const [additionalDetailsContent, setAdditionalDetailsContent] =
    useState("");
  const [additionalDetailsServiceName, setAdditionalDetailsServiceName] =
    useState("");
  const [showAccessoriesModal, setShowAccessoriesModal] = useState(false);
  const [viewAccessories, setViewAccessories] = useState<any[]>([]);
  const [accessoriesServiceName, setAccessoriesServiceName] = useState("");
  const [accessoriesServiceCategory, setAccessoriesServiceCategory] = useState("");

  const toggleVersion = (id: string) => {
    setExpandedVersion(expandedVersion === id ? null : id);

    // Update displayed quotation based on selected version
    if (quotation) {
      if (id === (quotation.id || quotation.quotation_service_id)) {
        // Current version selected
        setDisplayedQuotation(quotation);
      } else {
        // Historical version selected - use snapshot data
        const selectedVersion = quotation.quotation_service_versions?.find(
          (v: any) => (v.id || v.version_id) === id
        );

        if (selectedVersion?.quotation_snapshot) {
          // Use the snapshot data for historical versions
          const snapshotData = {
            ...selectedVersion.quotation_snapshot,
            quotation_service_items: selectedVersion.services_snapshot || [],
            // Override version information with the snapshot's version data
            major_version: selectedVersion.quotation_snapshot.major_version,
            minor_version: selectedVersion.quotation_snapshot.minor_version,
            version_string: selectedVersion.quotation_snapshot.version_string,
            // Keep version metadata
            version_type: selectedVersion.version_type,
            created_by_user: selectedVersion.created_by_user,
          };
          setDisplayedQuotation(snapshotData);
        }
      }
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedVersions((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const fetchSingleQuotationData = async () => {
    if (!id) return;
    setQuotation(null);
    setFetchError(null);
    setIsLoading(true);

    try {
      const response = await OrderAndSalesAxios.get(
        `${API_ENDPOINT.QUOTATION.SERVICE.DETAIL(id)}`,
        { params: { include_versions: true } }
      );
      const details = response.data.data;
      setQuotation(details);
      setDisplayedQuotation(details);

      // Set default expanded and selected version to the latest
      if (details) {
        const currentId = details.id || details.quotation_service_id;
        setExpandedVersion(currentId);
        setSelectedVersions([currentId]);
      }
    } catch (err: any) {
      console.error("Error fetching service quotation:", err);
      setFetchError(
        err?.message || "Failed to fetch service quotation details."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (
    quotationId: string,
    newStatus: string,
    remark?: string
  ) => {
    try {
      await dispatch(
        handleServiceQuotationStatus({
          id: quotationId,
          quotation_status: newStatus,
          remark: remark,
        })
      ).unwrap();
      fetchSingleQuotationData();
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  useEffect(() => {
    fetchSingleQuotationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const content = (
    <>
      <style>
        {`
.description-modal-content { overflow: auto; max-width: 100%; max-height: 70vh; word-wrap: break-word; }
.description-modal-content img { max-width: 100%; height: auto; object-fit: contain; }
        `}
      </style>
      {/* Header */}
      {!propId && (
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="link"
              className="p-0 text-muted"
              onClick={() => navigate(-1)}
              style={{ textDecoration: "none" }}
            >
              <ArrowLeft size={18} />
            </Button>
            <h5 className="mb-0" style={{ fontWeight: 600, fontSize: "20px" }}>
              View Quotation
            </h5>
          </div>
        </div>
      )}
      {!propId && <hr />}

      {isLoading ? (
        <LoadingSpinner label="Loading quotation details..." />
      ) : fetchError ? (
        <div className="d-flex justify-content-center align-items-center flex-column py-5">
          <div className="text-danger">{fetchError}</div>
          <Button
            variant="primary"
            className="mt-2"
            onClick={() => navigate(-1)}
            style={{
              backgroundColor: MecordColors.ThemeBGColor,
              color: "#fff",
              textDecoration: "none",
              border: "1px solid transparent",
              transition: "border-color 0.15s ease-in-out",
              fontWeight: 600,
              fontSize: "0.85rem",
            }}
          >
            Back
          </Button>
        </div>
      ) : !quotation ? (
        <div className="d-flex justify-content-center py-5">
          <p>No quotation found.</p>
        </div>
      ) : (
        <>
          {/* Versions Section */}
          <div className="mb-4">
            <h6 className="fw-bold mb-2" style={{ fontSize: "0.95rem" }}>
              Quotation Versions
            </h6>

            {(quotation
              ? [
                {
                  ...quotation,
                  version: "Current Version",
                  id: quotation.id || quotation.quotation_service_id,
                  isCurrent: true,
                },
                ...(quotation.quotation_service_versions || []).map(
                  (v: any) =>  ({
                    ...v,
                    id: v.id || v.version_id,
                    version: `Version ${v.version_string || ""}`,
                    created_at: v?.created_at,
                    isCurrent: false,
                  })
                ),
              ]
              : []
            ).map((v: any) => {
              // console.log("V",v)
              return (
               <Card
                 key={v.id}
                 className={`mb-2 border rounded-3 py-2 ${expandedVersion === v.id ? "border-light" : "border-light"
                   }`}
                 style={{ cursor: "pointer" }}
               >
                 <Card.Body className="py-2 px-3 gap-2">
                   <div
                     className="d-flex align-items-center justify-content-between"
                     onClick={() => toggleVersion(v.id)}
                     style={{ cursor: "pointer" }}
                   >
                     <div className="d-flex align-items-center gap-2">
                       <div
                         style={{
                           width: "20px",
                           height: "20px",
                           borderRadius: "50%",
                           border: "1.5px solid #ddd",
                           display: "flex",
                           alignItems: "center",
                           justifyContent: "center",
                         }}
                         onClick={(e) => {
                           e.stopPropagation();
                           toggleSelection(v.id);
                         }}
                       >
                         {selectedVersions.includes(v.id) && (
                           <div
                             style={{
                               width: "10px",
                               height: "10px",
                               borderRadius: "50%",
                               backgroundColor: "#0d6efd",
                             }}
                           />
                         )}
                       </div>
                       <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                         {v.version}
                       </span>
                     </div>
                     {expandedVersion === v.id ? (
                       <ChevronUp size={16} className="text-muted" />
                     ) : (
                       <ChevronDown size={16} className="text-muted" />
                     )}
                   </div>
                   <hr />
 
                   <div className="d-flex align-items-center gap-2 mt-2">
                     <div
                       className="d-flex align-items-center justify-content-center rounded"
                       style={{
                         width: "22px",
                         height: "22px",
                         backgroundColor: "#d1e7dd",
                         fontSize: "0.6rem",
                         fontWeight: 600,
                         color: "#0f5132",
                       }}
                     >
                       <User size={12} />
                     </div>
                     <div>
                       <div
                         style={{
                           fontWeight: 600,
                           fontSize: "0.75rem",
                           color: "#333",
                           lineHeight: 1.2,
                         }}
                       >
                         { v.isCurrent ? v.updated_by_user?.first_name : v?.created_by_user?.first_name}{" "}
                         { v.isCurrent ? v.updated_by_user?.last_name : v?.created_by_user?.last_name}
                       </div>
                       <div
                         style={{
                           color: "#666",
                           fontSize: "0.65rem",
                           lineHeight: 1.2,
                         }}
                       >
                        {
                          (() => {
                            console.log("Row Data:", v);

                            let dateValue = null;

                            if (v.isCurrent) {
                              dateValue = v.updated_at;
                            } else {
                              dateValue = v.created_at;
                            }

                            console.log("isCurrent:", v.isCurrent);
                            console.log("Date Used:", dateValue);

                            return dateValue && moment(dateValue).isValid()
                              ? moment(dateValue).format("DD MMM YYYY, HH:mm")
                              : "-";
                          })()
                        }
                       </div>
                     </div>
                   </div>
 
                   {expandedVersion === v.id && (
                     <div className="mt-3 ps-1">
                        {/* Quotation Details */}
                        <h5 className="mb-4">Quotation Details</h5>
                        <Row className="mb-4 g-2">
                          <Col md={6}>
                            <p>
                              <strong>Quotation No. :</strong>{" "}
                              {displayedQuotation?.quotation_number || "-"}
                            </p>
                            <p>
                              <strong>Service Number :</strong>{" "}
                              {displayedQuotation?.service_number || "-"}
                            </p>
                            {displayedQuotation?.uid_number && (
                              <p>
                                <strong>UID Number :</strong>{" "}
                                {displayedQuotation?.uid_number}
                              </p>
                            )}
                            <p>
                              <strong>Quotation Date :</strong>{" "}
                              {displayedQuotation?.quotation_date
                                ? moment(
                                  displayedQuotation.quotation_date
                                ).format("DD-MMM-YYYY")
                                : "-"}
                            </p>
                          </Col>
                          <Col md={6}>
                            <p>
                              <strong>Initiated By :</strong>{" "}
                              {displayedQuotation?.initiated_by || "-"}
                            </p>
                            <p>
                              <strong>Valid Until :</strong>{" "}
                              {displayedQuotation?.valid_until_date
                                ? moment(
                                  displayedQuotation.valid_until_date
                                ).format("DD-MMM-YY")
                                : "-"}
                            </p>
                            {displayedQuotation?.enquiry_number && (
                              <p>
                                <strong>Enquiry No. :</strong>{" "}
                                {displayedQuotation.enquiry_number}
                              </p>
                            )}
                            {displayedQuotation?.reference_number && (
                              <p>
                                <strong>Reference No. :</strong>{" "}
                                {displayedQuotation.reference_number}
                              </p>
                            )}
                          </Col>
                        </Row>
                        <hr />
 
                       {/* Customer details */}
                       <h5 className="mb-3">Customer Details</h5>
                       <div
                         className="p-3 mb-4"
                         style={{
                           backgroundColor: MecordColors.ActiveBackground,
                           borderRadius: "10px",
                         }}
                       >
                         <Row className="mb-3 g-2">
                           <Col md={6}>
                             <p>
                               <strong>Company Name :</strong>{" "}
                               {displayedQuotation?.customer_details
                                 ?.company_name || "-"}
                             </p>
                             <p>
                               <strong>Type :</strong>{" "}
                               {displayedQuotation?.customer_details?.gst_type ||
                                 "-"}
                             </p>
                             <p>
                               <strong>Company Email :</strong>{" "}
                               {displayedQuotation?.customer_details?.email ||
                                 displayedQuotation?.customer_details
                                   ?.company_email ||
                                 "-"}
                             </p>
                           </Col>
                           <Col md={6}>
                             <p>
                               <strong>Industry :</strong>{" "}
                               {displayedQuotation?.customer_details?.industry ||
                                 "-"}
                             </p>
                             <p>
                               <strong>Company Phone No. :</strong>{" "}
                               {displayedQuotation?.customer_details
                                 ?.phone_number || "-"}
                             </p>
                           </Col>
                         </Row>
                         <hr />
                         <h6 className="fw-bold mb-3">Contact Details</h6>
                         <Row>
                           <Col md={12}>
                             {displayedQuotation?.customer_details
                               ?.contacts &&
                               displayedQuotation?.customer_details
                                 ?.contacts.length > 0 ? (
                               (() => {
                                 const contacts =
                                   displayedQuotation?.customer_details
                                     ?.contacts || [];
                                 const chunkArray = (
                                   arr: any[],
                                   size: number
                                 ) => {
                                   const result = [];
                                   for (let i = 0; i < arr.length; i += size) {
                                     result.push(arr.slice(i, i + size));
                                   }
                                   return result;
                                 };
                                 return chunkArray(contacts, 2).map(
                                   (pair, rowIndex) => (
                                     <Row key={rowIndex} className="mb-3">
                                       <Col md={6}>
                                         {pair[0] && (
                                           <div className="mb-2">
                                             <p className="mb-1">
                                               <strong>Contact Name:</strong>{" "}
                                               {pair[0].name || "N/A"}
                                             </p>
                                             <p className="mb-1">
                                               <strong>Email:</strong>{" "}
                                               {pair[0].email || "N/A"}
                                             </p>
                                             <p className="mb-1">
                                               <strong>Phone Number:</strong>{" "}
                                               {pair[0].phone_number || "N/A"}
                                             </p>
                                           </div>
                                         )}
                                       </Col>
                                       <Col md={6}>
                                         {pair[1] && (
                                           <div className="mb-2">
                                             <p className="mb-1">
                                               <strong>Contact Name:</strong>{" "}
                                               {pair[1].name || "N/A"}
                                             </p>
                                             <p className="mb-1">
                                               <strong>Email:</strong>{" "}
                                               {pair[1].email || "N/A"}
                                             </p>
                                             <p className="mb-1">
                                               <strong>Phone Number:</strong>{" "}
                                               {pair[1].phone_number || "N/A"}
                                             </p>
                                           </div>
                                         )}
                                       </Col>
                                     </Row>
                                   )
                                 );
                               })()
                             ) : (
                               <p className="text-muted">
                                 No contact persons listed.
                               </p>
                             )}
                           </Col>
                         </Row>
                       </div>
                       <hr style={{ width: "100%" }} />
 
                        {/* Agent & Assign Member */}
                        <Row className="mb-0 g-3 align-items-start">
                          <Col md={6}>
                            <h5
                              className="fw-bold mb-4"
                              style={{ fontSize: "1rem" }}
                            >
                              Agent Details
                            </h5>
                            <div className="d-flex align-items-center ms-2">
                              <span
                                className="fw-bold"
                                style={{ minWidth: "100px", color: "#334155" }}
                              >
                                Agent :
                              </span>
                              <span>
                                {displayedQuotation?.agent_name || "N/A"}
                              </span>
                            </div>

                            {/* Assigned Engineers */}
                            {displayedQuotation?.assigned_technician && displayedQuotation.assigned_technician.length > 0 && (
                              <div className="mt-3">
                                <h5 className="fw-bold mb-2" style={{ fontSize: "1rem" }}>Assigned Engineers</h5>
                                <div className="d-flex flex-wrap gap-2 ms-2">
                                  {displayedQuotation.assigned_technician.map((tech: any, idx: number) => {
                                    const initials = `${tech.first_name?.[0] || ""}${tech.last_name?.[0] || ""}`.toUpperCase();
                                    const colors = [
                                      { bg: "#dbeafe", circle: "#2563eb" },
                                      { bg: "#dcfce7", circle: "#16a34a" },
                                      { bg: "#fce7f3", circle: "#db2777" },
                                      { bg: "#fef9c3", circle: "#ca8a04" },
                                    ];
                                    const color = colors[idx % colors.length];
                                    return (
                                      <div
                                        key={tech.user_id || idx}
                                        className="d-flex align-items-center p-1 pe-3 rounded"
                                        style={{ backgroundColor: color.bg, border: "none" }}
                                      >
                                        <div
                                          className="d-flex align-items-center justify-content-center rounded me-2"
                                          style={{
                                            width: "24px",
                                            height: "24px",
                                            backgroundColor: color.circle,
                                            color: "#fff",
                                            fontSize: "0.7rem",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          {initials || "T"}
                                        </div>
                                        <span style={{ fontSize: "0.85rem", fontWeight: "600", color: "#1e293b" }}>
                                          {tech.first_name} {tech.last_name}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </Col>
                         <Col md={6}>
                           <h5
                             className="fw-bold mb-4"
                             style={{ fontSize: "1rem" }}
                           >
                             Assign Member
                           </h5>
                           <div className="d-flex flex-wrap gap-2 ms-2">
                             {displayedQuotation?.assigned_members &&
                               displayedQuotation?.assigned_members.length > 0 ? (
                               displayedQuotation.assigned_members.map(
                                 (member: any, idx: number) => {
                                   const initials = `${member.first_name?.[0] || ""
                                     }${member.last_name?.[0] || ""
                                     }`.toUpperCase();
                                   const colors = [
                                     {
                                       bg: "#fee2e2",
                                       circle: "#ef4444",
                                       text: "#991b1b",
                                     },
                                     {
                                       bg: "#ccfbf1",
                                       circle: "#0d9488",
                                       text: "#115e59",
                                     },
                                     {
                                       bg: "#e0e7ff",
                                       circle: "#4f46e5",
                                       text: "#3730a3",
                                     },
                                     {
                                       bg: "#fef3c7",
                                       circle: "#d97706",
                                       text: "#92400e",
                                     },
                                   ];
                                   const color = colors[idx % colors.length];
 
                                   return (
                                     <div
                                       key={member.assigned_member_id || idx}
                                       className="d-flex align-items-center p-1 pe-3 rounded"
                                       style={{
                                         backgroundColor: color.bg,
                                         border: "none",
                                       }}
                                     >
                                       <div
                                         className="d-flex align-items-center justify-content-center rounded me-2"
                                         style={{
                                           width: "24px",
                                           height: "24px",
                                           backgroundColor: color.circle,
                                           color: "#fff",
                                           fontSize: "0.7rem",
                                           fontWeight: "bold",
                                         }}
                                       >
                                         {initials || "U"}
                                       </div>
                                       <span
                                         style={{
                                           fontSize: "0.85rem",
                                           fontWeight: "600",
                                           color: "#1e293b",
                                         }}
                                       >
                                         {member.first_name} {member.last_name}
                                       </span>
                                     </div>
                                   );
                                 }
                               )
                             ) : (
                               <span className="text-muted">
                                 No members assigned
                               </span>
                             )}
                           </div>
                         </Col>
                       </Row>

                       <Row className="mt-3 mb-3">
                       
                       <h5
                            className="fw-bold mb-4"
                            style={{ fontSize: "1rem" }}
                          >
                            Tags
                          </h5>
                          <div className="d-flex align-items-center ms-2">

                              {Array.isArray(displayedQuotation?.tags) &&
                              displayedQuotation.tags.length > 0 ? (
                                <span className="d-inline-flex flex-wrap gap-2 mt-1">
                                  {displayedQuotation.tags.map(
                                    (tag: string) => (
                                      <span
                                        key={tag}
                                        className="badge bg-light text-dark d-flex align-items-center rounded-2"
                                        style={{
                                          border: "1px solid #dee2e6",
                                          padding: "4px 8px",
                                          fontSize: "11px",
                                        }}
                                      >
                                        {tag}
                                      </span>
                                    )
                                  )}
                                </span>
                              ) : (
                                <span>-</span>
                              )}
                            </div>
                        </Row>

                       <hr />
 
                       {/* GST & Taxation Details */}
                       <Row className="mb-0">
                         <Col md={6}>
                           <h5 className="mb-3">GST & Taxation Details</h5>
                           <p>
                             <strong>Compositional GST :</strong>{" "}
                             {displayedQuotation?.customer_details?.gst_type ===
                               "Compositional gst"
                               ? "YES"
                               : displayedQuotation?.customer_details?.gst_type
                                 ? "NO"
                                 : "N/A"}
                           </p>
                         </Col>
                       </Row>
                       <hr />
 
                       {/* Service Items Table */}
                       <h5 className="mb-3">Services</h5>
                       <div
                         className="table-responsive p-3 mb-4"
                         style={{
                           backgroundColor: MecordColors.ActiveBackground,
                           borderRadius: "10px",
                         }}
                       >
                         <div
                           className="overflow-auto"
                         // style={{
                         //   "&::-webkit-scrollbar": {
                         //     height: "8px",
                         //   },
                         //   "&::-webkit-scrollbar-track": {
                         //     background: "#f1f1f1",
                         //     borderRadius: "4px",
                         //   },
                         //   "&::-webkit-scrollbar-thumb": {
                         //     background: "#888",
                         //     borderRadius: "4px",
                         //   },
                         //   "&::-webkit-scrollbar-thumb:hover": {
                         //     background: "#555",
                         //   },
                         // }}
                         >
                           <table
                             style={{
                               width: "100%",
                               minWidth: "2200px",
                               borderCollapse: "collapse",
                               fontSize: "0.75rem",
                               whiteSpace: "nowrap",
                             }}
                           >
                             <thead style={{ backgroundColor: "#f8f9fa" }}>
                               <tr>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Sr No.
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Model
                                 </th>
                                <th
                                  style={{
                                    padding: "8px",
                                    borderBottom: "1px solid #eee",
                                    textAlign: "left",
                                  }}
                                >
                                  Product
                                </th>
                                <th
                                  style={{
                                    padding: "8px",
                                    borderBottom: "1px solid #eee",
                                    textAlign: "left",
                                  }}
                                >
                                  Additional Details
                                </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Serial No.
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Service Category
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Service Method
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Service Type
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Execution Mode
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Quantity
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Service Rate
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Gross Value
                                 </th>
                                 {/* {(() => {
                                   // Show Discount/Flat column only if any item has percentage discount
                                   const items = quotation?.quotation_service_items || [];
                                   const hasPercentageDiscount = Array.isArray(items) && items.length > 0 && items.some(
                                     (item: any) => item?.discount_type === "percentage"
                                   );
                                   if (!hasPercentageDiscount) return null;
                                   return ( */}
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Discount/Flat
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Discount Value
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Net Value
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   GST(%)
                                 </th>
                                 {(() => {
                                   const custDetails = displayedQuotation?.customer_details || {};
                                   const billFrom = displayedQuotation?.bill_from || {};
                                   const billFromState = (billFrom.org_state || billFrom.state || "").toString().trim().toLowerCase();
                                   const billToState = (custDetails.billing_address?.state || "").toString().trim().toLowerCase();
                                   const isIntra = !!billFromState && !!billToState && billFromState === billToState;
                                   return isIntra ? (
                                     <>
                                       <th style={{ padding: "8px", borderBottom: "1px solid #eee", textAlign: "left" }}>SGST(%)</th>
                                       <th style={{ padding: "8px", borderBottom: "1px solid #eee", textAlign: "left" }}>CGST(%)</th>
                                     </>
                                   ) : (
                                     <th style={{ padding: "8px", borderBottom: "1px solid #eee", textAlign: "left" }}>IGST(%)</th>
                                   );
                                 })()}
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   GST Value
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Total Service Amount
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Service Address
                                 </th>
                                 <th
                                   style={{
                                     padding: "8px",
                                     borderBottom: "1px solid #eee",
                                     textAlign: "left",
                                   }}
                                 >
                                   Reported Complaints
                                 </th>
                               </tr>
                             </thead>
                             <tbody style={{ backgroundColor: "#fff" }}>
                               {displayedQuotation?.quotation_service_items?.map(
                                 (item: any, idx: number) => (
                                   <tr
                                     key={item.quotation_service_item_id || idx}
                                   >
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {idx + 1}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {item.model_name || "-"}
                                     </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                        verticalAlign: "middle",
                                      }}
                                    >
                                      <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start" }}>
                                        <div className="d-flex align-items-center gap-1">
                                          <div
                                            style={{
                                              fontWeight: 600,
                                              fontSize: "0.75rem",
                                            }}
                                          >
                                            {item.product_name ||
                                              item.serviceName ||
                                              "-"}
                                                       <div
                                          style={{
                                            color: "#666",
                                            fontSize: "0.65rem",
                                          }}
                                        >
                                          SKU: {item.product_sku || "-"} | HSN:{" "}
                                          {item.hsn_code || "-"}
                                        </div>
                                          </div>
                                          <InfoIcon
                                            style={{
                                              cursor: "pointer",
                                              color:
                                                tableStyles.primaryColor ||
                                                MecordColors?.ThemeBGColor,
                                              fontSize: "18px",
                                              flexShrink: 0,
                                            }}
                                            onClick={() => {
                                              const content =
                                                item.prod_description ??
                                                item.description ??
                                                "";
                                              setDescriptionModalContent(
                                                content
                                              );
                                              setDescriptionModalServiceName(
                                                item.product_name ||
                                                  item.serviceName ||
                                                  ""
                                              );
                                              setShowDescriptionModal(true);
                                            }}
                                          />
                                        </div>
                                        {Array.isArray(item.accessories) &&
                                          item.accessories.filter((a: any) => !a.is_deleted).length > 0 && (
                                            <Button
                                              variant="link"
                                              className="p-0 mt-1"
                                              style={{
                                                fontSize: "0.7rem",
                                                fontFamily: "Poppins, sans-serif",
                                                color:
                                                  tableStyles.primaryColor ||
                                                  MecordColors?.ThemeBGColor ||
                                                  "#AB2123",
                                                textDecoration: "underline",
                                              }}
                                              onClick={() => {
                                                const accs = (item.accessories || [])
                                                  .filter((a: any) => !a.is_deleted)
                                                  .map(mapApiAccessoryToItem);
                                                setViewAccessories(accs);
                                                setAccessoriesServiceName(
                                                  item.product_name || item.serviceName || ""
                                                );
                                                setAccessoriesServiceCategory(
                                                  item.service_category || item.serviceCategory || ""
                                                );
                                                setShowAccessoriesModal(true);
                                              }}
                                            >
                                              View Accessories (
                                              {item.accessories.filter((a: any) => !a.is_deleted).length})
                                            </Button>
                                          )}
                               
                                      </div>
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px",
                                        borderBottom: "1px solid #eee",
                                        verticalAlign: "middle",
                                      }}
                                    >
                                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <InfoIcon
                                          style={{
                                            cursor: "pointer",
                                            color:
                                              tableStyles.primaryColor ||
                                              MecordColors?.ThemeBGColor,
                                            fontSize: "18px",
                                          }}
                                          onClick={() => {
                                            const content =
                                              item.additional_details ?? "";
                                            setAdditionalDetailsContent(content);
                                            setAdditionalDetailsServiceName(
                                              item.product_name ||
                                                item.serviceName ||
                                                ""
                                            );
                                            setShowAdditionalDetailsModal(true);
                                          }}
                                        />
                                      </div>
                                    </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {item.serial_number || "-"}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {item.service_category || "-"}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {item.service_method || "-"}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {item.service_type || "-"}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {item.execution_mode || "-"}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {item.product_quantity || "-"}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {displayedQuotation?.currency_symbol || "₹"}{" "}
                                       {formatIndianAmount(item.service_rate)}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {displayedQuotation?.currency_symbol || "₹"}{" "}
                                       {formatIndianAmount(item.gross_value)}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {(() => {
                                         // discount_value is now the RAW input value (10 for 10% or 500 for ₹500)
                                         const discountValueInput = parseFloat(item.discount_value) || 0;
                                         const discountType = item.discount_type || "currency";
 
                                         if (discountType === "percentage") {
                                           // Display raw percentage value
                                           return `${formatIndianAmount(discountValueInput)}%`;
                                         } else {
                                           // Display "Flat" for currency/flat discounts
                                           return "Flat";
                                         }
                                       })()}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {(() => {
                                         // Calculate the actual discount amount from raw discount_value
                                         const discountValueInput = parseFloat(item.discount_value) || 0;
                                         const discountType = item.discount_type || "currency";
                                         const grossValue = parseFloat(item.gross_value) || 0;
 
                                         let calculatedDiscountAmount = 0;
                                         if (discountType === "percentage") {
                                           // Calculate: (gross * percentage) / 100
                                           calculatedDiscountAmount = (grossValue * discountValueInput) / 100;
                                         } else {
                                           // Currency discount - use raw value directly (capped at gross)
                                           calculatedDiscountAmount = Math.min(discountValueInput, grossValue);
                                         }
 
                                         return `${displayedQuotation?.currency_symbol || "₹"}${formatIndianAmount(calculatedDiscountAmount)}`;
                                       })()}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {displayedQuotation?.currency_symbol || "₹"}{" "}
                                       {formatIndianAmount(item.total_service_net_amount)}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {item.gst_percentage || "-"}
                                       {item.gst_percentage ? "%" : ""}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {displayedQuotation?.currency_symbol || "₹"}{" "}
                                       {formatIndianAmount(item.gst_value)}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {displayedQuotation?.currency_symbol || "₹"}{" "}
                                       {formatIndianAmount(item.total_service_amount)}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {item.shipping_address ? (
                                         <span style={{ fontSize: "0.7rem" }}>
                                           {item.shipping_address.address}
                                           {item.shipping_address.city &&
                                             `, ${item.shipping_address.city}`}
                                           {item.shipping_address.state &&
                                             `, ${item.shipping_address.state}`}
                                           {item.shipping_address.pincode &&
                                             ` - ${item.shipping_address.pincode}`}
                                         </span>
                                       ) : (
                                         "-"
                                       )}
                                     </td>
                                     <td
                                       style={{
                                         padding: "8px",
                                         borderBottom: "1px solid #eee",
                                       }}
                                     >
                                       {item.reported_complaints && item.reported_complaints.trim() !== "" ? (
                                         <Tooltip
                                           title={item.reported_complaints}
                                           arrow
                                           placement="top"
                                         >
                                           <div
                                             style={{
                                               overflow: "hidden",
                                               textOverflow: "ellipsis",
                                               whiteSpace: "nowrap",
                                               maxWidth: "150px",
                                               cursor: "pointer",
                                             }}
                                           >
                                             {item.reported_complaints}
                                           </div>
                                         </Tooltip>
                                       ) : (
                                         "-"
                                       )}
                                     </td>
                                   </tr>
                                 )
                               )}
                             </tbody>
                           </table>
                         </div>
                       </div>
 
 
 
                       {/* Totals Section */}
                       <Row className="mb-4">
                         <Col md={6}></Col>
                         <Col md={6}>
                           <div
                             className="p-3"
                             style={{
                               backgroundColor: MecordColors.ActiveBackground,
                               borderRadius: "10px",
                             }}
                           >
                             <div className="mb-2 fw-semibold" style={{ fontSize: "13px" }}>Service</div>
                             <Row>
                               <Col md={6}>
                                 <p>
                                   <strong>Sub Total (Before Discount) :</strong>
                                 </p>
                               </Col>
                               <Col md={6} className="text-end">
                                 <p>
                                   {displayedQuotation?.currency_symbol || "₹"}{" "}
                                   {formatIndianAmount(displayedQuotation?.service_sub_total ?? displayedQuotation?.sub_total_before_discount ?? 0)}
                                 </p>
                               </Col>
                             </Row>
                             <Row>
                               <Col md={6}>
                                 <p>
                                   <strong>Total Discount Value :</strong>
                                 </p>
                               </Col>
                               <Col md={6} className="text-end">
                                 <p>
                                   {displayedQuotation?.currency_symbol || "₹"}{" "}
                                   {formatIndianAmount(displayedQuotation?.service_discount_value ?? displayedQuotation?.total_discount_value ?? 0)}
                                 </p>
                               </Col>
                             </Row>
                             <Row>
                               <Col md={6}>
                                 <p>
                                   <strong>Net Total :</strong>
                                 </p>
                               </Col>
                               <Col md={6} className="text-end">
                                 <p>
                                   {displayedQuotation?.currency_symbol || "₹"}{" "}
                                   {formatIndianAmount(displayedQuotation?.service_net_value ?? displayedQuotation?.total_net_value ?? displayedQuotation?.net_total ?? 0)}
                                 </p>
                               </Col>
                             </Row>

                             {(() => {
                               const custDetails = displayedQuotation?.customer_details || {};
                               const isComp = custDetails.gst_type === "Compositional gst";
                               const billFrom = displayedQuotation?.bill_from || {};
                               const billFromState = (billFrom.org_state || billFrom.state || "").toString().trim().toLowerCase();
                               const billToState = (custDetails.billing_address?.state || "").toString().trim().toLowerCase();
                               const isIntra = !!billFromState && !!billToState && billFromState === billToState;
                               const srvGst = Number(displayedQuotation?.service_gst_value ?? displayedQuotation?.total_gst_value ?? 0);

                               if (isComp) {
                                 return null;
                               }
                               if (isIntra) {
                                 const half = Math.round((srvGst / 2) * 100) / 100;
                                 return (
                                   <>
                                     <Row>
                                       <Col md={6}><p><strong>CGST :</strong></p></Col>
                                       <Col md={6} className="text-end"><p>{displayedQuotation?.currency_symbol || "₹"} {formatIndianAmount(half)}</p></Col>
                                     </Row>
                                     <Row>
                                       <Col md={6}><p><strong>SGST :</strong></p></Col>
                                       <Col md={6} className="text-end"><p>{displayedQuotation?.currency_symbol || "₹"} {formatIndianAmount(half)}</p></Col>
                                     </Row>
                                   </>
                                 );
                               } else {
                                 return (
                                   <Row>
                                     <Col md={6}><p><strong>IGST :</strong></p></Col>
                                     <Col md={6} className="text-end"><p>{displayedQuotation?.currency_symbol || "₹"} {formatIndianAmount(srvGst)}</p></Col>
                                   </Row>
                                 );
                               }
                             })()}

                             <Row>
                               <Col md={6}>
                                 <p>
                                   <strong>Total GST Value :</strong>
                                 </p>
                               </Col>
                               <Col md={6} className="text-end">
                                 <p>
                                   {displayedQuotation?.currency_symbol || "₹"}{" "}
                                   {formatIndianAmount(displayedQuotation?.service_gst_value ?? displayedQuotation?.total_gst_value ?? 0)}
                                 </p>
                               </Col>
                             </Row>

                             <hr />
                             <div className="mb-2 fw-semibold" style={{ fontSize: "13px" }}>Accessories</div>
                             <Row>
                               <Col md={6}>
                                 <p>
                                   <strong>Sub Total (Before Discount) :</strong>
                                 </p>
                               </Col>
                               <Col md={6} className="text-end">
                                 <p>
                                   {displayedQuotation?.currency_symbol || "₹"}{" "}
                                   {formatIndianAmount(displayedQuotation?.accessory_sub_total ?? 0)}
                                 </p>
                               </Col>
                             </Row>
                             <Row>
                               <Col md={6}>
                                 <p>
                                   <strong>Total Discount Value :</strong>
                                 </p>
                               </Col>
                               <Col md={6} className="text-end">
                                 <p>
                                   {displayedQuotation?.currency_symbol || "₹"}{" "}
                                   {formatIndianAmount(displayedQuotation?.accessory_discount_value ?? 0)}
                                 </p>
                               </Col>
                             </Row>
                             <Row>
                               <Col md={6}>
                                 <p>
                                   <strong>Net Total :</strong>
                                 </p>
                               </Col>
                               <Col md={6} className="text-end">
                                 <p>
                                   {displayedQuotation?.currency_symbol || "₹"}{" "}
                                   {formatIndianAmount(displayedQuotation?.accessory_net_value ?? 0)}
                                 </p>
                               </Col>
                             </Row>

                             {(() => {
                               const custDetails = displayedQuotation?.customer_details || {};
                               const isComp = custDetails.gst_type === "Compositional gst";
                               const billFrom = displayedQuotation?.bill_from || {};
                               const billFromState = (billFrom.org_state || billFrom.state || "").toString().trim().toLowerCase();
                               const billToState = (custDetails.billing_address?.state || "").toString().trim().toLowerCase();
                               const isIntra = !!billFromState && !!billToState && billFromState === billToState;
                               const accGst = Number(displayedQuotation?.accessory_gst_value ?? 0);

                               if (isComp) {
                                 return null;
                               }
                               if (isIntra) {
                                 const half = Math.round((accGst / 2) * 100) / 100;
                                 return (
                                   <>
                                     <Row>
                                       <Col md={6}><p><strong>CGST :</strong></p></Col>
                                       <Col md={6} className="text-end"><p>{displayedQuotation?.currency_symbol || "₹"} {formatIndianAmount(half)}</p></Col>
                                     </Row>
                                     <Row>
                                       <Col md={6}><p><strong>SGST :</strong></p></Col>
                                       <Col md={6} className="text-end"><p>{displayedQuotation?.currency_symbol || "₹"} {formatIndianAmount(half)}</p></Col>
                                     </Row>
                                   </>
                                 );
                               } else {
                                 return (
                                   <Row>
                                     <Col md={6}><p><strong>IGST :</strong></p></Col>
                                     <Col md={6} className="text-end"><p>{displayedQuotation?.currency_symbol || "₹"} {formatIndianAmount(accGst)}</p></Col>
                                   </Row>
                                 );
                               }
                             })()}

                             <Row>
                               <Col md={6}>
                                 <p>
                                   <strong>Total GST Value :</strong>
                                 </p>
                               </Col>
                               <Col md={6} className="text-end">
                                 <p>
                                   {displayedQuotation?.currency_symbol || "₹"}{" "}
                                   {formatIndianAmount(displayedQuotation?.accessory_gst_value ?? 0)}
                                 </p>
                               </Col>
                             </Row>

                             <hr />
                             <Row>
                               <Col md={6}>
                                 <p>
                                   <strong>Grand Total Amount :</strong>
                                 </p>
                               </Col>
                               <Col md={6} className="text-end">
                                 <p className="fw-bold" style={{ fontSize: "1.2rem" }}>
                                   {displayedQuotation?.currency_symbol || "₹"}{" "}
                                   {formatIndianAmount(displayedQuotation?.grand_total_amount || 0)}
                                 </p>
                               </Col>
                             </Row>
                           </div>
                         </Col>
                       </Row>
 
                       <hr />
                       {/* T&C and Notes */}
                       <Row className="g-4">
                         <Col md={6}>
                           <h5 className="mb-3">Terms & Conditions</h5>
                           <div
                             className="p-3"
                             style={{
                               backgroundColor: "#fff",
                               borderRadius: "10px",
                               border: "1px solid #eee",
                             }}
                           >
                             {(() => {

                              const terms = displayedQuotation?.terms_and_conditions;

                              if (!terms || terms.length === 0) {
                                return (
                                  <div
                                    style={{
                                      fontSize: "0.8rem",
                                      color: "#999",
                                      textAlign: "center",
                                      padding: "10px 0",
                                    }}
                                  >
                                    No Terms & Conditions added.
                                  </div>
                                );
                              }

                               // Group terms by type to handle multiple options per type
                               const groupedTerms: Record<string, string[]> = {};
                               displayedQuotation?.terms_and_conditions?.forEach(
                                 (item: any) => {
                                   if (item.type) {
                                     if (!groupedTerms[item.type]) {
                                       groupedTerms[item.type] = [];
                                     }
                                     // Handle both string and array options
                                     if (Array.isArray(item.options)) {
                                       item.options.forEach((opt: string) => {
                                         if (
                                           opt &&
                                           !groupedTerms[item.type].includes(opt)
                                         ) {
                                           groupedTerms[item.type].push(opt);
                                         }
                                       });
                                     } else if (item.options) {
                                       // If options is a string, check if it's newline-separated
                                       const options = item.options
                                         .split("\n")
                                         .filter((opt: string) => opt.trim());
                                       options.forEach((opt: string) => {
                                         if (
                                           opt &&
                                           !groupedTerms[item.type].includes(opt)
                                         ) {
                                           groupedTerms[item.type].push(opt);
                                         }
                                       });
                                     }
                                   }
                                 }
                               );
 
                               return Object.entries(groupedTerms).map(
                                 ([type, options], idx) => (
                                   <div key={idx} className="mb-3">
                                     <strong
                                       style={{
                                         fontSize: "0.75rem",
                                         display: "block",
                                         marginBottom: "0.5rem",
                                       }}
                                     >
                                       {type}
                                     </strong>
                                     <ul
                                       style={{
                                         fontSize: "0.7rem",
                                         color: "#666",
                                         marginLeft: "20px",
                                         paddingLeft: "0",
                                         listStyle: "disc",
                                       }}
                                     >
                                       {options.map(
                                         (option: string, optIdx: number) => (
                                           <li
                                             key={optIdx}
                                             style={{ marginBottom: "4px" }}
                                           >
                                             {option}
                                           </li>
                                         )
                                       )}
                                     </ul>
                                     {idx <
                                       Object.keys(groupedTerms).length - 1 && (
                                         <hr
                                           style={{
                                             width: "100%",
                                             marginTop: "12px",
                                           }}
                                         />
                                       )}
                                   </div>
                                 )
                               );
                             })()}
                           </div>
                         </Col>
 
                         <Col md={6}>
                           <h5 className="mb-3">Notes & Attachments</h5>
                           <Table bordered className="bg-white">
                             <tbody>
                               <tr>
                                 <td
                                   style={{
                                     minWidth: "70px",
                                     fontWeight: 500,
                                     whiteSpace: "nowrap",
                                   }}
                                 >
                                   Notes
                                 </td>
                                 <td style={{ whiteSpace: "pre-wrap" }}>
                                   {displayedQuotation?.notes ||
                                     "N/A"}
                                 </td>
                               </tr>
                             </tbody>
                           </Table>
                           {displayedQuotation?.attachments &&
                             displayedQuotation.attachments.length > 0 ? (
                             <div className="mt-3">
                               <strong className="d-block mb-2">
                                 Attachments:
                               </strong>
                               <AttachmentList attachments={displayedQuotation.attachments} />
                               {/* <div className="d-flex flex-wrap gap-2">
                                 {displayedQuotation.attachments.map(
                                   (url: string, idx: number) => {
                                     const fileName = url.split("/").pop();
                                     return (
                                       <a
                                         key={idx}
                                         href={url}
                                         target="_blank"
                                         rel="noopener noreferrer"
                                         className="text-decoration-none"
                                       >
                                         <span
                                           className="badge bg-secondary"
                                           style={{ fontSize: "0.75rem" }}
                                         >
                                           {fileName || `Attachment ${idx + 1}`}
                                         </span>
                                       </a>
                                     );
                                   }
                                 )}
                               </div> */}
                             </div>
                           ) : displayedQuotation?.attachments &&
                             displayedQuotation.attachments.length > 0 ? (
                             <div className="mt-3">
                               <strong className="d-block mb-2">
                                 Attachments:
                               </strong>
                               <AttachmentList attachments={displayedQuotation.attachments} />
                               {/* <div className="d-flex flex-wrap gap-2">
                                 {quotation.attachments.map(
                                   (url: string, idx: number) => {
                                     const fileName = url.split("/").pop();
                                     return (
                                       <a
                                         key={idx}
                                         href={url}
                                         target="_blank"
                                         rel="noopener noreferrer"
                                         className="text-decoration-none"
                                       >
                                         <span
                                           className="badge bg-secondary"
                                           style={{ fontSize: "0.75rem" }}
                                         >
                                           {fileName || `Attachment ${idx + 1}`}
                                         </span>
                                       </a>
                                     );
                                   }
                                 )}
                               </div> */}
                             </div>
                           ) : (
                             <p className="text-muted mt-2">No attachments</p>
                           )}
                         </Col>
                       </Row>
                       <hr />
 
 
                     </div>
                   )}
                 </Card.Body>
               </Card>
             )
            }
            )}
          </div>

          {/* Service Description Modal (Info icon) */}
          <Modal
            show={showDescriptionModal}
            onHide={() => {
              setShowDescriptionModal(false);
              setDescriptionModalContent("");
              setDescriptionModalServiceName("");
            }}
            centered
          >
            <Modal.Header
              closeButton
              style={{
                fontFamily: tableStyles.header.fontFamily,
                fontSize: tableStyles.header.fontSize,
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <Modal.Title
                style={{
                  fontFamily: tableStyles.header.fontFamily,
                  fontSize: tableStyles.header.fontSize,
                  fontWeight: tableStyles.header.fontWeight,
                }}
              >
                Service Description
                {descriptionModalServiceName
                  ? ` - ${descriptionModalServiceName}`
                  : ""}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body
              style={{
                fontFamily: filterPopoverStyles.fontFamily,
                fontSize: filterPopoverStyles.fontSize,
                overflow: "hidden",
                maxWidth: "100%",
              }}
            >
              {descriptionModalContent ? (
                <div
                  className={`${DETAILS_BG} description-modal-content`}
                  style={{
                    minHeight: "80px",
                    fontFamily: filterPopoverStyles.fontFamily,
                    fontSize: filterPopoverStyles.fontSize,
                    overflow: "auto",
                    maxWidth: "100%",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(descriptionModalContent),
                  }}
                />
              ) : (
                <div
                  className="text-muted"
                  style={{
                    fontFamily: filterPopoverStyles.fontFamily,
                    fontSize: filterPopoverStyles.fontSize,
                  }}
                >
                  No description available.
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-center">
              <Button
                variant="secondary"
                className="rounded"
                style={{
                  fontFamily: filterPopoverStyles.button.fontFamily,
                  fontSize: filterPopoverStyles.button.fontSize,
                  borderColor: filterPopoverStyles.cancelButton.borderColor,
                  color: filterPopoverStyles.cancelButton.color,
                  backgroundColor: "#fff",
                }}
                onClick={() => {
                  setShowDescriptionModal(false);
                  setDescriptionModalContent("");
                  setDescriptionModalServiceName("");
                }}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Additional Details Modal (Info icon) */}
          <Modal
            show={showAdditionalDetailsModal}
            onHide={() => {
              setShowAdditionalDetailsModal(false);
              setAdditionalDetailsContent("");
              setAdditionalDetailsServiceName("");
            }}
            centered
          >
            <Modal.Header
              closeButton
              style={{
                fontFamily: tableStyles.header.fontFamily,
                fontSize: tableStyles.header.fontSize,
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <Modal.Title
                style={{
                  fontFamily: tableStyles.header.fontFamily,
                  fontSize: tableStyles.header.fontSize,
                  fontWeight: tableStyles.header.fontWeight,
                }}
              >
                Additional Details
                {additionalDetailsServiceName
                  ? ` - ${additionalDetailsServiceName}`
                  : ""}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body
              style={{
                fontFamily: filterPopoverStyles.fontFamily,
                fontSize: filterPopoverStyles.fontSize,
                overflow: "hidden",
                maxWidth: "100%",
              }}
            >
              {additionalDetailsContent ? (
                <div
                  className={`${DETAILS_BG} description-modal-content`}
                  style={{
                    minHeight: "80px",
                    fontFamily: filterPopoverStyles.fontFamily,
                    fontSize: filterPopoverStyles.fontSize,
                    overflow: "auto",
                    maxWidth: "100%",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(additionalDetailsContent),
                  }}
                />
              ) : (
                <div
                  className="text-muted"
                  style={{
                    fontFamily: filterPopoverStyles.fontFamily,
                    fontSize: filterPopoverStyles.fontSize,
                  }}
                >
                  No additional details available.
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-center">
              <Button
                variant="secondary"
                className="rounded"
                style={{
                  fontFamily: filterPopoverStyles.button.fontFamily,
                  fontSize: filterPopoverStyles.button.fontSize,
                  borderColor: filterPopoverStyles.cancelButton.borderColor,
                  color: filterPopoverStyles.cancelButton.color,
                  backgroundColor: "#fff",
                }}
                onClick={() => {
                  setShowAdditionalDetailsModal(false);
                  setAdditionalDetailsContent("");
                  setAdditionalDetailsServiceName("");
                }}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          <ViewServiceAccessoriesModal
            show={showAccessoriesModal}
            onHide={() => {
              setShowAccessoriesModal(false);
              setViewAccessories([]);
              setAccessoriesServiceName("");
              setAccessoriesServiceCategory("");
            }}
            serviceName={accessoriesServiceName}
            serviceCategory={accessoriesServiceCategory}
            accessories={viewAccessories}
            currencySymbol={"\u20b9"}
          />

          {/* Status Remarks and History - Hide in Draft view */}
          {displayedQuotation?.quotation_stage !== "Draft" && (
            <>
              <hr />
              <Row className="mt-3">
                <Col md={6}>
                  <section className="mb-4">
                    <h5 className="mb-3">Status Remarks and History</h5>
                    <StatusRemarksHistory
                      items={displayedQuotation?.status_remarks_and_history || []}
                    />
                  </section>
                </Col>
              </Row>
            </>
          )}

          {/* Back Button */}
          {!propId && <hr />}
          {!propId && (
            <Row>
              <div className="d-flex flex-wrap justify-content-center gap-3">
                 <CancelButton showConfirmation={false} onClick={()=>navigate(-1)} />
              </div>
            </Row>
          )}
        </>
      )}
    </>
  );

  return propId ? <div className="p-0">{content}</div> : <PageContainer>{content}</PageContainer>;
};

export default ViewServiceQuotation;
